// Libraries
import React from 'react';
import { connect } from 'react-redux';
import { generatePath, Redirect, RouteComponentProps, withRouter } from 'react-router-dom';

// Actions
import {
  attachOmniRiskProfile,
  attachRiskProfile,
  closeComparison,
  createComparison,
  DELETE_COMPARISON,
  getComparisonById,
  loadComparison,
  removeMissingComparisonData,
  rerunComparison,
  setTab as setComparisonTab,
  skipEmptyState,
} from '@/redux/modules/comparison';
import {
  deleteContact,
  deleteContactRiskProfile,
  getContactById,
  loadContactData,
  getContactActivity,
  getContactComparisons,
  getContactPortfolios,
  saveContactRiskProfile,
  setPersistenceSource,
  SET_IS_SAVING_CONTACT_RISK_PROFILE,
} from '@/redux/modules/contacts';
import {
  getKycQuestionnaires,
  loadKycQuestionnaire,
  resetRiskProfile,
  setRiskProfileForEdit,
  SET_IS_RESETTING_RISK_PROFILE,
  setRecommendedRiskProfile,
} from '@/redux/modules/kyc-questionnaire';
import { resetCurrentContact, resetGif } from '@/redux/modules/common/actionCreators';
import { selectIsLoadingEvery, selectIsLoadingSome, startLoaderAction, stopLoaderAction } from '@/redux/modules/ui/loaders';
import { setBaseDob } from '@/redux/modules/bmo-gif';

// Components
import ContactDetailsInfo from './ContactDetailsInfo';
import ContactDetailsTabs from './ContactDetailsTabs';
import ModalV2 from '@/views/components/modals/ModalV2';
import { NameRiskProfile } from '@/views/components/modals/risk-profile/components/NameRiskProfile';
import { ComparisonMissingItemsModal, CreateKycQuestionnaire, DeleteContactsModal } from '@/views/components/modals';
import { DestructiveModal } from '@capintel/component-lib/destructive-modal';
import { Stack } from '@capintel/component-lib/stack';
import { Header } from '@/views/components/layout';
import { LoadingSpinner } from '@/views/components/common';
import { SecondaryNavigation } from '@/views/components/navigation';

// Hooks
import useThemeVersion from '@/hooks/useThemeVersion';

// Enums
import { OmniRiskProfileStatus } from '@/enums/OmniRiskProfiles';

// Types
import Activity from '@/types/activity';
import AppMode from '@/types/app-mode';
import Comparison from '@/types/comparison';
import ComparisonContact from '@/types/comparison-contact';
import ContactRiskProfile from '@/types/contact-risk-profile';
import Dictionary from '@/types/dictionary';
import GenericContact from '@/types/generic-contact';
import KycQuestionnaire from '@/types/kyc-questionnaire';
import Locale from '@/types/locale';
import LocalizedString from '@/types/localized-string';
import NavigationItem from '@/types/navigation-item';
import Portfolio from '@/types/portfolio';
import ReducedComparison from '@/types/reduced-comparison';
import RiskProfileItem, { RISK_SCORE_TYPE, SelectedRiskProfile } from '@/types/risk-profile-item';
import User from '@/types/user';
import { CurrentContact } from '@/redux/modules/types/contacts';
import { FormattedFullOmniRiskProfile, Profile as PartialOmniRiskProfileConfig } from '@/types/risk-profiles/omni/full-omni-risk-profile';
import { ReduxState } from '@/redux/modules/types';
import { SortingField } from '@/types/data';

// Utils
import heapUtils, { HEAP_CUSTOM_EVENTS } from '@/utils/3rd-party/heap';
import localStorageUtils from '@/utils/local-storage';
import notification from '@/utils/notification';
import sessionStorage, { SessionStorageKey } from '@/utils/session-storage';
import { BMO_CONTACT_TYPE } from '@/utils/bmo-gif/constants';
import { getActiveQuestionnaire } from '@/utils/risk-profiles';
import { comparisonHasGifItems, comparisonHasMissingOrModifiedItems } from '@/utils/comparison';
import { COMPARISON_NAV_PATHS, NAV_PATHS } from '@/utils/constants';
import { CONTACT_LAYOUT, getContactLayout } from '@/utils/contacts';
import { formatLocalizedString, formatMessage, FormattedMessage } from '@/utils/i18n';
import { PERMISSIONS, userHasPermission, userHasPermissionEnabled } from '@/utils/permissions';
import { PERSISTENCE_KEYS } from '@/utils/persistence';
import { showError } from '@/utils/errors';

// Legacy component to deprecate
import LegacyContactDetails from './LegacyContactDetails';
import { addUpRiskToProfile } from '@/utils/up-risk';
import LoadingScreen from '@/views/components/common/LoadingScreen';

type MatchParams = {
  contactId: string;
};

type StateProps = {
  currentContact?: CurrentContact;
  questionnaires: KycQuestionnaire[];
  loadedComparison?: Comparison;
  locale: Locale;
  loading: {
    current: {
      portfolios: boolean;
      comparisons: boolean;
      activityLogs: boolean;
      nextContactPortfoliosToken?: string;
      nextContactComparisonsToken?: string;
    };
  };
  newRiskProfile?: Partial<ContactRiskProfile>;
  recommendedRiskProfile?: RiskProfileItem;
  currentQuestionnaire?: KycQuestionnaire;
  isDeletingContactComparison: boolean;
  isSavingRiskProfile?: boolean;
  persistenceSource?: string;
};

type DispatchProps = {
  getContactById: (id: string) => Promise<GenericContact>;
  loadContactData: (contact: GenericContact) => Promise<void>;
  deleteContact: (contact: GenericContact) => Promise<void>;
  resetCurrentContact: () => void;
  getContactComparisons: (contact: GenericContact, query?: string) => Promise<Comparison[]>;
  getContactPortfolios: (query?: string, sort?: SortingField[], shouldFetchNext?: boolean) => Promise<Portfolio[]>;
  getKycQuestionnaires: () => Promise<KycQuestionnaire[]>;
  loadKycQuestionnaire: (questionnaire: KycQuestionnaire) => void;
  resetRiskProfile: () => void;
  saveContactRiskProfile: (profile: Partial<ContactRiskProfile>) => Promise<ContactRiskProfile[]>;
  deleteContactRiskProfile: (profile: ContactRiskProfile) => Promise<void>;
  attachOmniRiskProfile: (profile: FormattedFullOmniRiskProfile) => Promise<void>;
  attachRiskProfile: (profile: RiskProfileItem) => Promise<void>;
  setRiskProfileForEdit: (profile: ContactRiskProfile) => void;
  getContactActivity: (contact: GenericContact, nextToken?: string) => Promise<Activity[]>;
  skipEmptyState: () => void;
  resetGif: () => void;
  setBaseDob: (contact: ComparisonContact) => void;
  setComparisonTab: (path?: string) => void;
  startLoaderAction: (name: string, params: Dictionary<unknown>) => void;
  stopLoaderAction: (name: string) => void;
  setPersistenceSource: (user: User, key: string, value?: string) => void;

  // Comparisons dispatch props
  // TODO: In an effort to bring all logic related to comparison in ContactDetailsComparisons.tsx
  // These are only called by onViewComparison and by removeMissingItemsAndLoadComparison
  getComparisonById: (id: string, contact?: ComparisonContact) => Promise<Comparison>;
  loadComparison: (comparison: Comparison) => Promise<Comparison>;
  rerunComparison: (comparison: Comparison) => Promise<Comparison>;
  removeMissingComparisonData: (comparison: Comparison) => Promise<Comparison>;
  closeComparison: (comparison: Comparison, resetContact?: boolean) => void;
  setRecommendedRiskProfile: (riskProfile: SelectedRiskProfile) => void;
};

type Props = RouteComponentProps<MatchParams> &
  StateProps &
  DispatchProps & {
    user: User;
    isHouseholdContext?: boolean;
  };

type State = {
  redirect?: string;
  riskProfileName?: string;
  searchParams?: string;
  selectedRiskProfileForDelete?: ContactRiskProfile;
  showDeleteContactModal: boolean;
  showComparisonMissingItemsModal: boolean;
  showNameRiskProfileModal: boolean;
  showRiskProfileModal: boolean;
  showDeleteRiskProfileModal: boolean;
  pendingComparison: boolean;
  attachingRiskProfile: boolean;
  isChangingRiskProfile: boolean;
  selectedRiskProfileForComparison?: ContactRiskProfile | PartialOmniRiskProfileConfig;
};

class ContactDetailsV2 extends React.Component<Props, State> {
  readonly state: State = {
    showDeleteContactModal: false,
    showNameRiskProfileModal: false,
    showRiskProfileModal: false,
    showDeleteRiskProfileModal: false,
    showComparisonMissingItemsModal: false,
    pendingComparison: false,
    attachingRiskProfile: false,
    isChangingRiskProfile: false,
  };

  constructor(props: Props) {
    super(props);

    this.loadContactFromParams();
    this.props.getKycQuestionnaires();
  }

  componentWillUnmount() {
    // Reset current contact in Redux when navigation is NOT performed to a comparison page path
    if (this.props.history.location.pathname.indexOf(NAV_PATHS.COMPARISON) === -1) {
      this.props.resetCurrentContact();
    }
  }

  loadContactFromParams = () => {
    const { user } = this.props;
    const { contactId } = this.props.match.params;

    // We want to make sure to set the Persistence Key in redux if the contact id has one.
    if (user != null && contactId != null) {
      const userPersistenceKeys = user?.appConfig.persistenceKeys;
      const persistenceSource = localStorageUtils.getPersistenceSource(user, localStorageUtils.STORAGE_KEYS.CONTACT_PERSISTENCE_SOURCE);
      if (userPersistenceKeys?.includes(PERSISTENCE_KEYS.EXISTING_CLIENT_CONTACT) && persistenceSource == null) {
        const regex = new RegExp('^[A-Za-z0-9+/]+={0,2}$');
        if (regex.test(contactId)) {
          const decoded = atob(contactId);
          const testString = `${PERSISTENCE_KEYS.EXISTING_CLIENT_CONTACT}-`;
          if (decoded.startsWith(testString)) {
            this.props.setPersistenceSource(user, localStorageUtils.STORAGE_KEYS.CONTACT_PERSISTENCE_SOURCE, PERSISTENCE_KEYS.EXISTING_CLIENT_CONTACT);
          }
        }
      }
    }

    if (contactId != null && user.appConfig.mode === AppMode.CONTACT && userHasPermission(user, PERMISSIONS.PERSIST_CONTACT_ID_IN_SESSION)) {
      sessionStorage.setItem(SessionStorageKey.CONTACT_ID, contactId);
    }

    this.props
      .getContactById(contactId)
      .then(contact => {
        this.props.loadContactData(contact);
      })
      .catch(err => {
        if (err.status === 404) {
          this.setState({ redirect: NAV_PATHS.CONTACTS });
        } else {
          showError(err);
        }
      });
  };

  renderLoading = () => <LoadingSpinner />;

  closeNameRiskProfileModal = () => this.setState({ showNameRiskProfileModal: false });

  showNameRiskProfileModal = () => this.setState({ showNameRiskProfileModal: true });

  getNavItems = (): NavigationItem[] => {
    const { user, currentContact } = this.props;

    if (!currentContact) {
      return [];
    }

    const label =
      formatMessage('ContactDetails.Header.BackTo') +
      formatMessage(`Contacts.ContactTabs.Breadcrumb.${getContactLayout(user) === CONTACT_LAYOUT.CLIENT ? 'Clients' : 'Contacts'}`);

    const navItems = [];
    if (user.appConfig.mode !== AppMode.CONTACT) {
      navItems.push({
        label,
        value: NAV_PATHS.CONTACTS,
        icon: 'icon-arrow-left',
      });
    }

    return navItems;
  };

  handleNameRiskProfileSubmit = async (name: string) => {
    this.setState({ riskProfileName: name, showRiskProfileModal: true });
    this.closeNameRiskProfileModal();
  };

  onDeleteContact = () => {
    this.setState({ showDeleteContactModal: true });
  };

  onCancelDeleteContact = () => {
    this.setState({ showDeleteContactModal: false });
  };

  onConfirmDeleteContact = () => {
    const { currentContact } = this.props;

    if (!currentContact) {
      return;
    }

    this.props.deleteContact(currentContact.contact).then(() => {
      this.props.resetCurrentContact();
      this.setState({ redirect: NAV_PATHS.CONTACTS });
      notification.success(formatMessage('Contacts.DeleteModal.DeleteOneSuccess'));
    });
  };

  onLoadMoreComparisons = (query?: string) => {
    const { currentContact } = this.props;

    if (!currentContact) {
      return Promise.reject();
    }

    return this.props.getContactComparisons(currentContact.contact, query || undefined);
  };

  onLoadMoreActivityLogs = () => {
    const { currentContact } = this.props;

    if (!currentContact) {
      return Promise.reject();
    }

    return this.props.getContactActivity(currentContact.contact, currentContact.nextActivityLogToken);
  };

  onSearchComparisons = (query: string) => {
    const { currentContact } = this.props;

    if (!currentContact) {
      return Promise.reject();
    }

    return this.props.getContactComparisons(currentContact.contact, query);
  };

  // Risk profiles props
  // TODO: In an effort to bring all logic related to risk profiles in RiskProfilesTable.tsx
  // These are only called by onAttachRiskProfile
  onAttachRiskProfile = (riskProfile: ContactRiskProfile) => {
    this.setState({ attachingRiskProfile: true, selectedRiskProfileForComparison: riskProfile });
    this.props
      .attachRiskProfile({
        ...riskProfile.profile,
        savedSections: riskProfile.savedSections,
        questionnaireId: riskProfile.questionnaire,
        externalData: riskProfile.externalData,
      })
      .then(() => {
        this.setState({ attachingRiskProfile: false, redirect: NAV_PATHS.COMPARISON });
      });
  };

  onAttachOmniRiskProfile = (omniRiskProfile: FormattedFullOmniRiskProfile) => {
    if (omniRiskProfile.status !== OmniRiskProfileStatus.Approved) return;
    this.setState({ attachingRiskProfile: true, selectedRiskProfileForComparison: omniRiskProfile.profile! });
    this.props.attachOmniRiskProfile(omniRiskProfile).then(() => {
      this.setState({ attachingRiskProfile: false, redirect: NAV_PATHS.COMPARISON });
    });
  };

  // Risk profiles props
  // TODO: In an effort to bring all logic related to risk profiles in RiskProfilesTable.tsx
  // These are only called by onDeleteRiskProfile
  onDeleteRiskProfile = (riskProfile: ContactRiskProfile) => {
    this.setState({
      selectedRiskProfileForDelete: riskProfile,
      showDeleteRiskProfileModal: true,
    });
  };

  // Risk profiles props
  // TODO: In an effort to bring all logic related to risk profiles in RiskProfilesTable.tsx
  // These are only called by onEditRiskProfile
  onEditRiskProfile = (riskProfile: ContactRiskProfile) => {
    const { questionnaires, user, setRecommendedRiskProfile, loadKycQuestionnaire, setRiskProfileForEdit } = this.props;
    const questionnaire = questionnaires.find(q => q._id === riskProfile.questionnaire);

    if (!questionnaire) {
      notification.errorFromKey('QUESTIONNAIRE_NOT_FOUND');
      return;
    }

    loadKycQuestionnaire(questionnaire);
    setRiskProfileForEdit(riskProfile);
    if (userHasPermissionEnabled(user, PERMISSIONS.KYC_UP_RISK_WARNING)) {
      setRecommendedRiskProfile(riskProfile.profile);
    }
    this.setState({ isChangingRiskProfile: true, showRiskProfileModal: true });
  };

  // Risk profiles props
  // TODO: In an effort to bring all logic related to risk profiles in RiskProfilesTable.tsx
  // These are only called by onCreateRiskProfile
  onCreateRiskProfile = (questionnaire?: KycQuestionnaire) => {
    const { questionnaires, loadKycQuestionnaire } = this.props;

    if (questionnaire) {
      loadKycQuestionnaire(questionnaire);
    } else {
      loadKycQuestionnaire(getActiveQuestionnaire(questionnaires));
    }

    this.showNameRiskProfileModal();
  };

  onSaveRiskProfile = () => {
    const { newRiskProfile, recommendedRiskProfile, locale, user } = this.props;
    const { isChangingRiskProfile, riskProfileName } = this.state;
    let name: LocalizedString = {};

    if (isChangingRiskProfile) {
      name = { ...(newRiskProfile?.name || {}) };
      this.setState({ isChangingRiskProfile: false });
    } else {
      name = { ...(newRiskProfile?.name || {}), [locale]: riskProfileName };
    }

    if (newRiskProfile?.profile) {
      if (recommendedRiskProfile && userHasPermissionEnabled(user, PERMISSIONS.KYC_UP_RISK_WARNING)) {
        addUpRiskToProfile(newRiskProfile.profile, recommendedRiskProfile, RISK_SCORE_TYPE.ORDER);
      }

      this.props
        .saveContactRiskProfile({ ...newRiskProfile, name })
        .then(() => {
          this.props.resetRiskProfile();

          this.onCloseCreateRiskProfile();
          notification.success(formatMessage('CreateRiskProfile.SaveSuccess'));
        })
        .catch(() => {
          notification.error(formatMessage('CreateRiskProfile.SaveError'));
        });
    }
  };

  onCloseCreateRiskProfile = () => {
    this.props.resetRiskProfile();
    this.setState({ riskProfileName: undefined, showRiskProfileModal: false });
  };

  onCancelDeleteRiskProfile = () => {
    this.setState({
      showRiskProfileModal: false,
      selectedRiskProfileForDelete: undefined,
    });
  };

  onConfirmDeleteRiskProfile = async () => {
    const { selectedRiskProfileForDelete } = this.state;

    if (!selectedRiskProfileForDelete) {
      return;
    }

    await this.props.deleteContactRiskProfile(selectedRiskProfileForDelete);
    this.onCancelDeleteRiskProfile();

    notification.success(formatMessage('Contacts.ContactTabs.RiskProfiles.DeleteSuccess'));
  };

  /// /////////////////////////
  // TODO: In an effort to bring all logic relate to comparison in ContactDetailsComparisons.tsx
  // we must bring the next two functions in that file

  onViewComparison = async (comparison: ReducedComparison) => {
    const { currentContact } = this.props;
    if (!comparison._id) {
      return;
    }

    this.props.startLoaderAction('OPEN_COMPARISON', { comparisonId: comparison._id });
    const dbComparison = await this.props.getComparisonById(comparison._id, comparison.contact);
    const newComparison = await this.props.loadComparison(dbComparison);

    if (comparisonHasMissingOrModifiedItems(newComparison)) {
      this.setState({ showComparisonMissingItemsModal: true });
      this.props.stopLoaderAction('OPEN_COMPARISON');
      return;
    }

    const isGIF = comparisonHasGifItems(dbComparison);

    if (isGIF) {
      this.props.skipEmptyState();
      this.props.resetGif();
    }

    const subPath = isGIF ? COMPARISON_NAV_PATHS.GIF : COMPARISON_NAV_PATHS.PERFORMANCE;

    this.props.setComparisonTab(subPath);

    heapUtils.track(HEAP_CUSTOM_EVENTS.LOAD_EXISTING_COMPARISON);

    this.props.stopLoaderAction('OPEN_COMPARISON');
    const pathname = generatePath(NAV_PATHS.COMPARISON_DETAILS, {
      comparisonId: newComparison._id || '',
    });
    const contactQuery = currentContact ? `?contact=${currentContact.contact._id}` : '';
    this.setState({
      redirect: `${pathname}${subPath}`,
      searchParams: contactQuery,
    });
  };

  removeMissingItemsAndLoadComparison = async () => {
    const originalComparison = this.props.loadedComparison;
    if (!originalComparison) {
      return;
    }

    let newComparison = await this.props.removeMissingComparisonData(originalComparison);
    newComparison = await this.props.rerunComparison(newComparison);

    this.props.closeComparison(originalComparison, false);
    await this.props.loadComparison(newComparison);

    this.setState({ showComparisonMissingItemsModal: false, redirect: NAV_PATHS.COMPARISON });
  };

  // END TODO
  /// /////////////////////////

  renderDetailsContent = (currentContact: CurrentContact) => {
    const { user, loading, locale, questionnaires } = this.props;
    const isGIF = userHasPermissionEnabled(user, PERMISSIONS.BMO_GIF) && currentContact?.contact?.type === BMO_CONTACT_TYPE;
    const isOmniRiskProfile = userHasPermissionEnabled(user, PERMISSIONS.OMNI_RISK_PROFILE);

    return (
      <>
        <ContactDetailsInfo user={user} isGIF={isGIF} locale={locale} contact={currentContact.contact} onDelete={this.onDeleteContact} />

        <ContactDetailsTabs
          user={user}
          locale={locale}
          questionnaires={questionnaires}
          currentContact={currentContact}
          onLoadMoreComparisons={this.onLoadMoreComparisons}
          onLoadMoreActivityLogs={this.onLoadMoreActivityLogs}
          loading={loading.current}
          attachingRiskProfile={this.state.attachingRiskProfile}
          selectedRiskProfileForComparison={this.state.selectedRiskProfileForComparison as ContactRiskProfile}
          isOmniRiskProfile={isOmniRiskProfile}
          onViewComparison={this.onViewComparison}
          onSearchComparisons={this.onSearchComparisons}
          onAttachRiskProfile={this.onAttachRiskProfile}
          onAttachOmniRiskProfile={this.onAttachOmniRiskProfile}
          onDeleteRiskProfile={this.onDeleteRiskProfile}
          onEditRiskProfile={this.onEditRiskProfile}
          onCreateRiskProfile={this.onCreateRiskProfile}
          // TODO LEGACY DEPRECATION: Remove this props when we remove Legacy Components
          onCreateComparison={() => {}}
          onDeleteComparison={() => {}}
          onRerunComparison={() => {}}
          openingComparisonIds={[]}
          rerunningComparisonIds={[]}
        />
      </>
    );
  };

  render() {
    const { currentContact, locale, currentQuestionnaire, isHouseholdContext, newRiskProfile, loadedComparison, location } = this.props;

    const {
      redirect,
      searchParams,
      showDeleteContactModal,
      showNameRiskProfileModal,
      showRiskProfileModal,
      selectedRiskProfileForDelete,
      showDeleteRiskProfileModal,
      showComparisonMissingItemsModal,
    } = this.state;

    if (redirect) {
      return (
        <Redirect
          push
          to={{
            pathname: redirect,
            search: searchParams ?? undefined,
            state: { from: location },
          }}
        />
      );
    }

    return (
      <>
        <div className="contact-details-v2">
          {!isHouseholdContext && <Header />}

          <main id="content" className="contact-details-container">
            <div className="contact-details-body">
              {currentContact ? (
                <>
                  {!isHouseholdContext && <SecondaryNavigation items={this.getNavItems()} className="breadcrumb-nav" />}

                  <div className="contact-details-content">{this.renderDetailsContent(currentContact)}</div>
                </>
              ) : (
                <LoadingScreen titleToLocalize={isHouseholdContext ? 'ContactDetails.Loading.GetHousehold' : 'ContactDetails.Loading.GetContact'} />
              )}
            </div>
          </main>
        </div>

        {showDeleteContactModal && currentContact && (
          <DeleteContactsModal
            show={showDeleteContactModal}
            onCancel={this.onCancelDeleteContact}
            onConfirm={this.onConfirmDeleteContact}
            contacts={[currentContact.contact]}
          />
        )}

        {showNameRiskProfileModal && currentContact && (
          <ModalV2
            body={<NameRiskProfile closeModal={this.closeNameRiskProfileModal} onSubmit={this.handleNameRiskProfileSubmit} />}
            onClose={this.closeNameRiskProfileModal}
            show={this.state.showNameRiskProfileModal}
            title="RiskProfileModal.NameRiskProfile.Title"
          />
        )}

        {showRiskProfileModal && currentQuestionnaire && newRiskProfile && (
          <CreateKycQuestionnaire
            isModal
            show={showRiskProfileModal}
            current={currentContact}
            onSave={this.onSaveRiskProfile}
            onClose={this.onCloseCreateRiskProfile}
            riskProfiles={currentQuestionnaire.riskProfiles}
            newRiskProfile={newRiskProfile}
            minRiskCalculation={currentQuestionnaire.options.minRiskCalculation}
            scoreCalculation={currentQuestionnaire.options.scoreCalculation}
            isSaving={this.props.isSavingRiskProfile}
          />
        )}

        {selectedRiskProfileForDelete && (
          <DestructiveModal
            open={showDeleteRiskProfileModal}
            onOpenChange={open => !open && this.onCancelDeleteRiskProfile()}
            title={formatMessage('DeleteRiskProfile.Title')}
            content={
              <Stack gap="150">
                {(() => {
                  const isFalsyObject = (obj: object) => Object.values(obj).every(value => !value);
                  const riskProfileLabel = [selectedRiskProfileForDelete.name, selectedRiskProfileForDelete.profile?.title].find(
                    label => label && !isFalsyObject(label),
                  );
                  const formattedLabel = formatLocalizedString(riskProfileLabel, locale);
                  return formattedLabel ? (
                    <>
                      <FormattedMessage id="DeleteRiskProfile.Body" />
                      <ul>
                        <li>{formattedLabel}</li>
                      </ul>
                    </>
                  ) : null;
                })()}
                <FormattedMessage id="DeleteRiskProfile.Confirmation" />
              </Stack>
            }
            confirmButtonText={formatMessage('DeleteRiskProfile.ConfirmButton')}
            cancelButtonText={formatMessage('Common.Cancel')}
            onConfirm={this.onConfirmDeleteRiskProfile}
            onCancel={this.onCancelDeleteRiskProfile}
          />
        )}

        {showComparisonMissingItemsModal && loadedComparison && (
          <ComparisonMissingItemsModal
            show={showComparisonMissingItemsModal}
            onCancel={() => {
              this.setState({ showComparisonMissingItemsModal: false });
              this.props.closeComparison(loadedComparison, false);
            }}
            onConfirm={this.removeMissingItemsAndLoadComparison}
            comparison={loadedComparison}
          />
        )}
      </>
    );
  }
}

const ContactDetails: React.FC<React.ComponentProps<typeof ContactDetailsV2>> = props => {
  const { isLightVersion } = useThemeVersion();
  if (isLightVersion) {
    return <ContactDetailsV2 {...props} />;
  }
  return <LegacyContactDetails {...props} />;
};

const mapStateToProps = (state: ReduxState): StateProps => {
  const loadedComparison = state.comparison.activeComparison;

  return {
    loadedComparison,
    questionnaires: state.kycQuestionnaire.questionnaires,
    newRiskProfile: state.kycQuestionnaire.newRiskProfile,
    recommendedRiskProfile: state.kycQuestionnaire.recommendedRiskProfile,
    currentQuestionnaire: state.kycQuestionnaire.currentQuestionnaire,
    currentContact: state.contacts.current,
    loading: state.contacts.loading,
    locale: state.intl.locale,
    isDeletingContactComparison: selectIsLoadingEvery(state, [DELETE_COMPARISON.type]),
    isSavingRiskProfile: selectIsLoadingSome(state, [SET_IS_SAVING_CONTACT_RISK_PROFILE.type, SET_IS_RESETTING_RISK_PROFILE.type]),
  };
};

export default withRouter(
  connect(mapStateToProps, {
    getContactById,
    loadContactData,
    deleteContact,
    getContactPortfolios,
    getContactComparisons,
    createComparison,
    getComparisonById,
    loadComparison,
    rerunComparison,
    getKycQuestionnaires,
    loadKycQuestionnaire,
    resetRiskProfile,
    saveContactRiskProfile,
    deleteContactRiskProfile,
    attachRiskProfile,
    attachOmniRiskProfile,
    setRiskProfileForEdit,
    getContactActivity,
    skipEmptyState,
    resetGif,
    setBaseDob,
    resetCurrentContact,
    setComparisonTab,
    startLoaderAction,
    stopLoaderAction,
    removeMissingComparisonData,
    closeComparison,
    setPersistenceSource,
    setRecommendedRiskProfile,
  })(ContactDetails),
);
